/**
 * AI arama sorgu servisinin çekirdeği — **widget'ın kendi kopyası.**
 *
 * Bu dosya bilinçli olarak panelin `lib/ai-search/query-client.ts` dosyasının
 * ikizidir. Neden kopya:
 *
 * - Widget **kendi kendine yeter** olmalı. `widget/ai-search/` klasörü bir gün
 *   ayrı bir repoya taşınabilir; o gün panelin `lib/`'ine uzanan bir import
 *   olmaması gerekiyor.
 * - Panel refactor'u müşteriye giden bir bundle'ı kıramaz.
 *
 * **Sözleşme tek kaynaktan doğrulanır:** `txt/ai-search-v1-response.txt`
 * (canlı yanıt örnekleri, 2026-10-01). Servis sözleşmesi değişirse İKİ dosya
 * da güncellenir:
 *   - widget/ai-search/src/search-core.ts   (bu dosya)
 *   - lib/ai-search/query-client.ts         (panel tarafı)
 *
 * Hiçbir şey import etmez: framework yok, env yok, yalnızca `fetch` ve
 * `AbortSignal`. Base URL parametre olarak gelir.
 */

/**
 * Gösterilecek sonuç sayısı. Servis `limit` parametresi almıyor; sayfa boyutu
 * mağazanın arama ayarındaki `perPage` (20/40/60/100). İlk N sonuç istemcide
 * kesiliyor — arayüzde gösterilen satır sayısı bu.
 */
export const MIN_SEARCH_LIMIT = 1;
export const MAX_SEARCH_LIMIT = 20;
export const DEFAULT_SEARCH_LIMIT = 10;

/**
 * Tek arama sonucu. Şekil canlı yanıtla doğrulandı (bkz.
 * `txt/ai-search-v1-response.txt`, 2026-10-01). Alanlar backend'in servise
 * yazdığı ürün sözleşmesinden (`TsoftSearchProductMapper`) gelir.
 */
export interface IAiSearchQueryResult {
  /** T-Soft `ProductId` (string). */
  id: string;
  title: string;
  /** Ürünün mağazadaki tam adresi. SEO linki yoksa `null`. */
  url: string | null;
  /** Tam URL. Görseli olmayan üründe `null`. */
  image: string | null;
  /** T-Soft `ProductCode`; yoksa `null`. */
  code: string | null;
  brand: string | null;
  model: string | null;
  category: string | null;
  categories: string[];
  /** Kartın alt satırı — mağaza ayarındaki `card` alanının değeri. */
  subtitle: string | null;
  /** KDV dahil satış fiyatı. **Para birimi alanı yok.** */
  price: number;
  /** İndirimsiz fiyat; indirim yoksa `price` ile aynı. */
  list_price: number;
  /** İndirim yüzdesi; yoksa 0. */
  discount: number;
  in_stock: boolean;
  /** 0-5. */
  rating: number;
  reviews: number;
  /** Varyant/mağaza filtreleri: `{ "Ağırlık": ["450 Gr"] }`. */
  options: Record<string, string[]>;
  /**
   * Ürünün hangi yolla geldiği. Aramada yanıtın `intent`'iyle aynı; arama
   * çubuğunda `word` (kelime) ya da `stem` (kök).
   */
  match: string;
  /**
   * Sabitleme kuralıyla öne çıkarıldı (ayar `pins`). Yalnız "Önerilen"
   * sıralamada ve filtre seçili değilken; "Öne çıkarıldı" etiketi için.
   */
  pinned?: boolean;
}

export interface IAiSearchFacetValue {
  /** `value` tipinde filtre değeri; `price`'ta `"0-100"`, `rating`'de 4/3/2. */
  value: string | number;
  /** `price` ve `rating`'de hazır etiket ("0 - 100 TL", "4 yıldız ve üstü"). */
  label?: string;
  /** Seçili olup o an sonuçta bulunmayan değerde `null`. */
  count: number | null;
  selected: boolean;
}

/**
 * Sol filtre. Tipler (servis `src/search.mjs` `listing()`):
 * - `value` — `category`, `brand`, `model`, `opt_*`: çoklu seçim, `values` listesi.
 * - `price` — fiyat dilimleri, çoklu seçim (`price=0-100`).
 * - `rating` — tek seçim (`rating=4`), sayılar "ve üstü" birikimli.
 * - `toggle` — `in_stock`, `discount`: yalnız `count` ve `selected`.
 */
export interface IAiSearchFacet {
  key: string;
  label: string;
  type: "value" | "price" | "rating" | "toggle";
  /** `/v1/facet-values` ile filtre içinde arama yapılabilir mi. */
  searchable?: boolean;
  values?: IAiSearchFacetValue[];
  count?: number;
  selected?: boolean;
}

/**
 * Arama yanıtı. `intent` aramanın hangi yolla çalıştığını söyler: niyet
 * eşleşmesi (`code`, `title`, `model`, `brand`, `category` — sonuç o değerle
 * sınırlı), kelime (`text`), yalnız anlam araması (`ai`), boş sorgu (`all`)
 * ya da sonuç yok (`none`, `suggestion` dolu olabilir).
 */
export interface AiSearchQueryResponse {
  query: string;
  page: number;
  per_page: number;
  sort: string;
  sorts: { key: string; label: string }[];
  intent: string;
  intentLabel: string;
  /** Toplam eşleşen ürün; `results` yalnız bu sayfa. */
  found: number;
  results: IAiSearchQueryResult[];
  facets: IAiSearchFacet[];
  /** "Bunu mu demek istediniz" önerisi; yoksa `null`. */
  suggestion: string | null;
  personalized: boolean;
  /** Servis süresi (ms). */
  ms: number;
}

/**
 * Servisin döndüğü hata. `status` HTTP kodu, `code` gövdedeki `error` dizesi:
 * `401 invalid_key`, `403 origin_not_allowed`, `403 licence_expired`,
 * `429 rate_limit_exceeded` (`Retry-After`), `402 insufficient_ai_credit`,
 * `503 billing_unavailable` (son ikisi yalnız AI arama ya da kişiselleştirme
 * açık mağazada). Kullanıcıya ne gösterileceğine çağıran karar verir.
 */
export class AiSearchQueryError extends Error {
  status: number;
  code: string | null;
  /** 429'da `Retry-After` saniye cinsinden; servis bu başlığı CORS'ta expose ediyor. */
  retryAfterSeconds: number | null;

  constructor(
    status: number,
    code: string | null,
    retryAfterSeconds: number | null,
  ) {
    super(code ?? `AI search query failed with status ${status}`);
    this.name = "AiSearchQueryError";
    this.status = status;
    this.code = code;
    this.retryAfterSeconds = retryAfterSeconds;
  }

  /** Bakiye/faturalama kaynaklı mı (`insufficient_ai_credit`, `billing_unavailable`). */
  get isBillingError() {
    return this.status === 402 || this.code === "billing_unavailable";
  }

  /** Hız sınırı — geri çekilip mağazanın kendi aramasına düşmek için. */
  get isRateLimited() {
    return this.status === 429;
  }

  /** Anahtar geçersiz/iptal edilmiş (`invalid_key`). */
  get isAuthError() {
    return this.status === 401;
  }

  /**
   * İstek mağazanın alan adından gelmedi (`origin_not_allowed`). Servis yalnız
   * `https://alan.com` ve `https://www.alan.com` kökenini kabul ediyor.
   */
  get isOriginError() {
    return this.status === 403 && this.code === "origin_not_allowed";
  }

  /** Mağazanın AI arama lisansı bitmiş (`licence_expired`). */
  get isLicenceError() {
    return this.status === 403 && this.code === "licence_expired";
  }
}

/** Gösterilecek sonuç sayısını 1–20 aralığına sıkıştırır. */
export const clampSearchLimit = (limit: number | undefined) => {
  if (limit == null) return DEFAULT_SEARCH_LIMIT;
  const truncated = Math.trunc(limit);
  if (!Number.isFinite(truncated) || truncated === 0) return DEFAULT_SEARCH_LIMIT;
  return Math.min(MAX_SEARCH_LIMIT, Math.max(MIN_SEARCH_LIMIT, truncated));
};

/** Sondaki `/` atılır; `${base}/v1/search` kurulumu tek tırnakta kalsın. */
export const normalizeBaseUrl = (baseUrl: string) =>
  baseUrl.replace(/\/+$/, "");

export interface IRunSearchQueryParams {
  /** Servis kökü, örn. `https://aisearch.sendheap.com`. */
  baseUrl: string;
  /** Mağazanın arama anahtarı (64 hex), `x-search-key` başlığıyla gider. */
  searchKey: string;
  /** Arama metni. */
  q: string;
  /** İstemcide kesilecek sonuç sayısı, 1–20. */
  limit?: number;
  /**
   * Giriş yapmış müşterinin e-postası — kişiselleştirme açıksa "Önerilen"
   * sıralama ona göre düzenlenir. Kapalıysa yok sayılır.
   */
  email?: string;
  signal?: AbortSignal;
}

/** Arama çubuğu yanıtı (`/v1/autocomplete`). */
export interface IAiSearchAutocompleteResponse {
  query: string;
  /** Tamamlama önerileri; `type` ürün adı ya da alan rolü. */
  suggestions: { text: string; type: string; typeLabel: string }[];
  /** Ayardaki `suggest` grupları (marka, kategori…) ve ürün sayıları. */
  groups: {
    type: string;
    label: string;
    items: { name: string; count: number }[];
  }[];
  products: IAiSearchQueryResult[];
  found: number;
  personalized?: boolean;
  ms?: number;
}

/** Filtre içinde arama (`/v1/facet-values?field=&fq=&q=`). */
export interface IAiSearchFacetValuesResponse {
  field: string;
  values: IAiSearchFacetValue[];
  ms?: number;
}

/** Mağazanın etiketleri ve sıralamaları (`/v1/config`), ücretsiz. */
export interface IAiSearchConfigResponse {
  name: string;
  fields: Record<string, { label: string; plural: string | null }>;
  sorts: { key: string; label: string }[];
  perPage: number;
}

export interface IRunSearchServiceRequestParams {
  baseUrl: string;
  searchKey: string;
  /** `/v1/search`, `/v1/autocomplete`, `/v1/facet-values`, `/v1/config`. */
  path: string;
  /** Çoklu filtreler için aynı anahtar birden çok kez eklenebilir. */
  params?: URLSearchParams;
  signal?: AbortSignal;
}

/**
 * Açık uçlara genel istek — başlık `x-search-key`. Hata gövdesi
 * `{ error: string }`; ayrım `response.ok` ile yapılıyor.
 *
 * İstek **mağazanın alan adından** gelmeli; başka kökenden 403
 * `origin_not_allowed`. AI arama ya da kişiselleştirme açıksa `search`,
 * `autocomplete` ve `facet-values` istekleri kredi harcar (`config` ücretsiz).
 */
export const runSearchServiceRequest = async <T>({
  baseUrl,
  searchKey,
  path,
  params,
  signal,
}: IRunSearchServiceRequestParams): Promise<T> => {
  const query = params?.toString();
  const response = await fetch(
    `${normalizeBaseUrl(baseUrl)}${path}${query ? `?${query}` : ""}`,
    {
      method: "GET",
      headers: { Accept: "application/json", "x-search-key": searchKey },
      signal,
    },
  );

  const body: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const errorCode = (body as { error?: unknown } | null)?.error;
    const retryAfter = Number(response.headers.get("Retry-After"));
    throw new AiSearchQueryError(
      response.status,
      typeof errorCode === "string" ? errorCode : null,
      Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : null,
    );
  }

  return body as T;
};

/**
 * `GET {baseUrl}/v1/search?q=…` — gömülü arama için kısa yol: yalnız sorgu
 * (ve kişiselleştirme e-postası) gönderir, ilk `limit` sonucu döndürür.
 * Cache/debounce gibi maliyet önlemleri çağıran katmanın işi —
 * `createSearchClient` bunları sağlıyor.
 */
export const runSearchQuery = async ({
  baseUrl,
  searchKey,
  q,
  limit,
  email,
  signal,
}: IRunSearchQueryParams): Promise<AiSearchQueryResponse> => {
  const params = new URLSearchParams({ q });
  if (email) params.set("email", email);

  const data = await runSearchServiceRequest<AiSearchQueryResponse>({
    baseUrl,
    searchKey,
    path: "/v1/search",
    params,
    signal,
  });
  return {
    ...data,
    results: (data.results ?? []).slice(0, clampSearchLimit(limit)),
  };
};

/**
 * `GET {baseUrl}/health` — anahtar ve köken istemez.
 * Yanıt: `{ status: "ok" }`.
 */
export const fetchSearchHealth = async (
  baseUrl: string,
  signal?: AbortSignal,
) => {
  const response = await fetch(`${normalizeBaseUrl(baseUrl)}/health`, {
    method: "GET",
    headers: { Accept: "application/json" },
    signal,
  });

  if (!response.ok) throw new AiSearchQueryError(response.status, null, null);

  return (await response.json()) as { status: string };
};
